'use client';

import { PublicPageLayout } from "@letsgo/PublicPageLayout";
import { Box, Typography, Container, Grid } from '@mui/material';
import { FeatureCard } from "@letsgo/FeatureCard";
import { featuresData, testimonialsData } from "../../lib/featuresData";

const FeaturesPage = () => {
  const categories = Array.from(new Set(featuresData.map(feature => feature.category)));

  return (
    <PublicPageLayout>
      <Box sx={{ py: 8, textAlign: 'center', bgcolor: 'background.default' }}>
        <Container maxWidth="md">
          <Typography variant="h3" fontWeight={700} mb={3} color="text.primary">
            Unleash Your Creativity with FloAI Studio Features
          </Typography>
          <Typography variant="h6" color="text.secondary" mb={5}>
            Discover a comprehensive suite of AI-powered tools and features designed to streamline your content creation, enhance your analytics, and boost your social media presence.
          </Typography>
        </Container>
      </Box>

      {/* Features by Category */}
      {categories.map(category => (
        <Box key={category} sx={{ py: 8, bgcolor: 'background.paper' }}>
          <Container maxWidth="lg">
            <Typography variant="h4" fontWeight={700} mb={6} textAlign="center" color="text.primary">
              {category}
            </Typography>
            <Grid container spacing={3} alignItems="stretch">
              {featuresData.filter(feature => {
                const typedStatus = feature.status as 'available' | 'premium' | 'coming_soon';
                return feature.category === category && typedStatus !== 'coming_soon';
              }).map(feature => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={feature.id} sx={{ display: 'flex' }}>
                  <FeatureCard feature={feature as typeof feature & { status: 'available' | 'premium' | 'coming_soon' }} />
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      ))}

      {/* What's Coming Next */}
      <Box sx={{ py: 8, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Typography variant="h4" fontWeight={700} mb={6} textAlign="center" color="text.primary">
            What&apos;s Coming Next
          </Typography>
          <Grid container spacing={3} alignItems="stretch">
            {featuresData.filter(feature => {
              const typedStatus = feature.status as 'available' | 'premium' | 'coming_soon';
              return typedStatus === 'coming_soon';
            }).map(feature => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={feature.id} sx={{ display: 'flex' }}>
                <FeatureCard feature={feature as typeof feature & { status: 'available' | 'premium' | 'coming_soon' }} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Testimonials: show section only when data is loaded from API/CMS */}
      {testimonialsData.length > 0 && (
        <Box sx={{ py: 8, bgcolor: 'background.paper' }}>
          <Container maxWidth="lg">
            <Typography variant="h4" fontWeight={700} mb={6} textAlign="center" color="text.primary">
              What Our Creators Say
            </Typography>
            <Grid container spacing={3} alignItems="stretch">
              {testimonialsData.map(testimonial => (
                <Grid item xs={12} sm={6} md={4} key={testimonial.id} sx={{ display: 'flex' }}>
                  <Box sx={{
                    p: 3,
                    borderRadius: 2,
                    bgcolor: 'background.default',
                    height: '100%',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    boxShadow: 3,
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 6,
                    }
                  }}>
                    <Typography variant="body1" color="text.primary" sx={{ fontStyle: 'italic', mb: 2, flexGrow: 1 }}>
                      &quot;{testimonial.quote}&quot;
                    </Typography>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={700} color="primary.main">
                        - {testimonial.author}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {testimonial.role}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      )}
    </PublicPageLayout>
  );
};

export default FeaturesPage;
